import { z } from 'zod';

import { contactsApi } from '@/api/queries/contacts';
import { importJobsApi, type ImportFormat } from '@/api/queries/importJobs';
import { useAppForm } from '@/components/form';
import { useModal } from '@/components/modal/useModal';
import { Button, Icon } from '@/components/ui';

import { useCanManageContacts } from '../useCanManageContacts';
import { BulkJobProgressDialog } from './BulkJobProgressDialog';
import { ContactDeleteImportResultDialog } from './ContactDeleteImportResultDialog';

const importSchema = z
  .object({
    importFormat: z.enum(['standard', 'ecomail', 'simple', 'profile', 'delete', 'json']),
    importSource: z.string().min(1, 'Import source is required'),
    file: z.instanceof(File).nullable(),
  })
  .superRefine((val, ctx) => {
    const isJson = val.importFormat === 'json';
    const label = isJson ? 'JSON' : 'CSV';
    if (!val.file) {
      ctx.addIssue({ code: z.ZodIssueCode.custom, path: ['file'], message: `Please select a ${label} file` });
      return;
    }
    const okType = isJson
      ? val.file.type === 'application/json' || val.file.name.toLowerCase().endsWith('.json')
      : val.file.type === 'text/csv' || val.file.name.toLowerCase().endsWith('.csv');
    if (!okType)
      ctx.addIssue({ code: z.ZodIssueCode.custom, path: ['file'], message: `Please select a ${label} file` });
  });

type ImportForm = z.input<typeof importSchema>;

const FORMAT_OPTIONS = [
  { value: 'standard', label: 'Standard CSV' },
  { value: 'simple', label: 'Simple CSV' },
  { value: 'ecomail', label: 'Ecomail CSV' },
  { value: 'profile', label: 'Profile CSV' },
  { value: 'json', label: 'Full JSON' },
];

// Only shown to admins.
const DELETE_FORMAT_OPTION = { value: 'delete', label: 'Delete by email' };

const FORMAT_DESCRIPTIONS: Record<string, string> = {
  standard:
    'A CSV with a header row. Recognized columns: firstName, lastName, primaryEmail (required), phone, tags (semicolon-separated), active, doNotContact. Rows without an email are skipped.',
  simple:
    'A plain CSV — no header needed. Email goes in the first column, phone in the second (optional). Nothing else is imported. A leading "email,phone" header row is detected and skipped.',
  ecomail:
    'An Ecomail export CSV (semicolon-delimited) with columns for email, name, surname, gender, birthday, city, street, zip, country, phone, tags, status, and timestamps.',
  profile:
    'A CSV with a header row, matched on Email. Imported columns: Gender, Region, Country, EducationInstitution, Profession, Specialization, Company, NoteSummary, SpecializationInterest, Rating. FirstName, LastName and PhoneNumber are ignored. For existing contacts these fields are overwritten with the CSV values.',
  delete:
    'A plain CSV of emails (one per line, first column). Each matching contact is soft-deleted. A leading "email" header row is detected and skipped. Admin only — this cannot be undone from the UI.',
  json:
    'A JSON array of full contact records, as produced by "Export to JSON". Matched by id then primary email; existing contacts have only their empty fields filled, and new contacts keep the exported id.',
};

export function ContactImportFormDialog() {
  const { closeModal, openModal } = useModal();
  const startImportMutation = importJobsApi.useStartImportJob();
  const deleteImportMutation = contactsApi.useImportDelete();
  const canManage = useCanManageContacts();
  const formatOptions = canManage ? [...FORMAT_OPTIONS, DELETE_FORMAT_OPTION] : FORMAT_OPTIONS;

  const form = useAppForm({
    defaultValues: {
      importFormat: 'standard',
      importSource: 'csv_import',
      file: null,
    } as ImportForm,
    validators: {
      onSubmit: importSchema,
    },
    onSubmit: async ({ value }) => {
      const file = value.file ?? new File([], '');
      if (value.importFormat === 'delete') {
        const result = await deleteImportMutation.mutateAsync({ file });
        openModal(<ContactDeleteImportResultDialog result={result} />);
        return;
      }
      // Imports now run as background jobs: start the job, then hand off to the
      // shared progress dialog (live progress + final created/updated/skipped
      // stats over SignalR). "Run in background" closes it; the top-bar jobs
      // indicator + completion toast take over.
      const job = await startImportMutation.mutateAsync({
        file,
        source: value.importSource,
        format: value.importFormat as ImportFormat,
      });
      openModal(<BulkJobProgressDialog jobId={job.id} initialTotal={job.total} />);
    },
  });

  const downloadTemplate = (format: 'standard' | 'simple' | 'profile') => {
    let csv: string;
    let fileName: string;
    if (format === 'simple') {
      csv =
        'email,phone\n' +
        'john.doe@example.com,+420775055212\n' +
        'jane.smith@example.com,';
      fileName = 'contacts_simple_template.csv';
    } else if (format === 'profile') {
      csv =
        'FirstName,LastName,Email,PhoneNumber,Gender,Region,Country,EducationInstitution,Profession,Specialization,Company,NoteSummary,SpecializationInterest,Rating\n' +
        'John,Doe,john.doe@example.com,+420775055212,male,Prague,Czechia,Charles University,Physician,Cardiology,General Hospital,Met at conference,Heart failure,4.5\n' +
        'Jane,Smith,jane.smith@example.com,,female,London,UK,Imperial College,Researcher,Oncology,Royal Hospital,Referred by partner,Immunotherapy,5';
      fileName = 'contacts_profile_template.csv';
    } else {
      csv =
        'firstName,lastName,titleBefore,titleAfter,birthDate,residence,primaryEmail,tags,active,doNotContact,verified\n' +
        'John,Doe,Dr.,,1980-01-15,New York,john.doe@example.com,"vip,customer",true,false,true\n' +
        'Jane,Smith,Prof.,PhD,1975-05-20,London,jane.smith@example.com,partner,true,false,true';
      fileName = 'contacts_template.csv';
    }
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = fileName;
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <form.AppForm>
      <form.FormRoot>
        <div className="ds-modal-head">
          <h2>Import contacts</h2>
        </div>
        <div className="ds-modal-body">
          <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
            <form.AppField name="importFormat">
              {(field) => <field.RadioGroup row options={formatOptions} />}
            </form.AppField>

            <form.Subscribe selector={(s) => s.values.importFormat}>
              {(importFormat) => (
                <>
                  <Banner>{FORMAT_DESCRIPTIONS[importFormat] ?? FORMAT_DESCRIPTIONS.standard}</Banner>
                  {(importFormat === 'standard' ||
                    importFormat === 'simple' ||
                    importFormat === 'profile') && (
                    <Button
                      variant="secondary"
                      size="sm"
                      onClick={() => downloadTemplate(importFormat)}
                      leftIcon={<Icon name="download" size={13} />}
                    >
                      Download CSV template
                    </Button>
                  )}
                </>
              )}
            </form.Subscribe>

            <form.Subscribe selector={(s) => s.values.importFormat}>
              {(importFormat) =>
                importFormat === 'delete' ? null : (
                  <form.AppField name="importSource">
                    {(field) => (
                      <field.TextField
                        label="Import source"
                        required
                        placeholder="e.g., csv_import, conference_2024"
                        helperText="Specify where these contacts originated from"
                      />
                    )}
                  </form.AppField>
                )
              }
            </form.Subscribe>

            <form.Subscribe selector={(s) => s.values.importFormat}>
              {(importFormat) => {
                const isJson = importFormat === 'json';
                return (
                  <form.AppField name="file">
                    {(field) => (
                      <field.FileInput
                        id="import-file-input"
                        accept={isJson ? '.json' : '.csv'}
                        placeholder={`Click to select a ${isJson ? 'JSON' : 'CSV'} file`}
                      />
                    )}
                  </form.AppField>
                );
              }}
            </form.Subscribe>

            {(startImportMutation.isError || deleteImportMutation.isError) && (
              <Banner tone="error">
                {(startImportMutation.error ?? deleteImportMutation.error) instanceof Error
                  ? (startImportMutation.error ?? deleteImportMutation.error)?.message
                  : 'Import failed'}
              </Banner>
            )}
          </div>
        </div>
        <div className="ds-modal-foot">
          <span style={{ flex: 1 }} />
          <Button variant="ghost" onClick={closeModal}>
            Cancel
          </Button>
          <form.Subscribe selector={(s) => ({ isSubmitting: s.isSubmitting, canSubmit: s.canSubmit })}>
            {({ isSubmitting, canSubmit }) => (
              <Button type="submit" variant="primary" disabled={!canSubmit} loading={isSubmitting}>
                {isSubmitting ? 'Importing...' : 'Import'}
              </Button>
            )}
          </form.Subscribe>
        </div>
      </form.FormRoot>
    </form.AppForm>
  );
}

function Banner({ children, tone = 'info' }: { children: React.ReactNode; tone?: 'info' | 'error' }) {
  const isError = tone === 'error';
  return (
    <div
      style={{
        display: 'flex',
        alignItems: 'flex-start',
        gap: 8,
        padding: '10px 12px',
        background: isError ? 'var(--red-soft)' : 'var(--brand-soft)',
        color: isError ? 'var(--red)' : 'var(--brand-ink)',
        borderRadius: 'var(--radius-sm)',
        fontSize: 12.5,
        lineHeight: 1.45,
      }}
    >
      <Icon name={isError ? 'alert' : 'info'} size={14} style={{ marginTop: 1, flexShrink: 0 }} />
      <span>{children}</span>
    </div>
  );
}
